import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { generateProformaInvoicePDF } from '../utils/pdfGenerator';
import { useDebounce } from '../hooks/useDebounce';
import { useAuth } from '../context/AuthContext';

const STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled','on_hold'];
const statusColors = { pending:'warning', confirmed:'info', processing:'info', shipped:'primary', delivered:'success', cancelled:'error', on_hold:'neutral' };

const DownloadIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

function OrderDetailModal({ order, onClose }) {
  if (!order) return null;
  const items = order.items || [];
  return (
    <Modal open onClose={onClose} title={`Proforma Invoice — ${order.order_number}`} size="lg">
      <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fff7ed', borderRadius: 8, border: '1px solid #fed7aa', fontSize: '0.82rem', color: '#9a3412', fontWeight: 600 }}>
        Proforma Invoice — prices and quantities are estimates and subject to change before final invoice is raised.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 16 }}>
        {[
          ['Client', order.client_name],
          ['Status', <span key="s" className={`badge badge-${statusColors[order.status] || 'neutral'}`}>{order.status}</span>],
          ['Quote #', order.quote_number || '—'],
          ['Quote Date', order.quote_date ? new Date(order.quote_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'],
          ['PO / Ref #', order.po_number || '—'],
          ['PO Date', order.po_date ? new Date(order.po_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'],
          ['Currency', order.currency || 'USD'],
          ['Expected Delivery', order.expected_delivery ? new Date(order.expected_delivery).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'],
          ['Created', new Date(order.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })],
        ].map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: '0.88rem', color: 'var(--text-primary)' }}>{value}</div>
          </div>
        ))}
      </div>
      {order.notes && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-secondary)', borderRadius: 6, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          <strong>Notes:</strong> {order.notes}
        </div>
      )}
      <table className="table" style={{ marginBottom: 0 }}>
        <thead><tr><th>#</th><th>Item</th><th>Cat #</th><th>Pack</th><th>Qty</th><th>Est. Price</th><th>Disc%</th><th>Est. Total</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={it.id}>
              <td>{i + 1}</td>
              <td style={{ fontWeight: 500 }}>{it.item_name || it.catalog_item_name || '—'}</td>
              <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{it.catalog_number || '—'}</td>
              <td>{it.pack_size || '—'}</td>
              <td style={{ textAlign: 'center' }}>{it.quantity}</td>
              <td style={{ textAlign: 'right' }}>{order.currency} {Number(it.price).toFixed(2)}</td>
              <td style={{ textAlign: 'center' }}>{it.discount_percent || 0}%</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{order.currency} {Number(it.total).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginRight: 16 }}>
          Subtotal: <strong>{order.currency} {Number(order.subtotal || 0).toFixed(2)}</strong>
          {Number(order.discount_percent) > 0 && <> | Discount ({order.discount_percent}%): <strong>-{order.currency} {Number(order.discount_amount || 0).toFixed(2)}</strong></>}
        </span>
        <span style={{ fontSize: '1rem', fontWeight: 700, color: '#c2410c' }}>
          Est. Total: {order.currency} {Number(order.total || 0).toFixed(2)}
        </span>
      </div>
    </Modal>
  );
}

export default function ProformaInvoiceOrders() {
  const { user, isPrivileged } = useAuth();
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [viewOrder, setViewOrder] = useState(null);

  // Assignment + Work Log state
  const [assignModal, setAssignModal] = useState({ open: false, orderId: null, orderNum: '', ownerId: null });
  const [assignments, setAssignments] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignTab, setAssignTab] = useState('assignments');
  const [assignForm, setAssignForm] = useState({ user_id: '', access_level: 'read_only', notes: '' });
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [wlForm, setWlForm] = useState({ log_type: 'note', log_text: '' });
  const [wlSaving, setWlSaving] = useState(false);
  const [wlError, setWlError] = useState('');

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/orders', {
        params: {
          order_type: 'proforma_invoice',
          page,
          limit: 15,
          search: debouncedSearch || undefined,
          status: statusFilter || undefined,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
        },
      });
      setData(res);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const openView = async (o) => {
    const { data: res } = await api.get(`/orders/${o.id}`);
    setViewOrder(res.data);
  };

  const handleDownload = async (o) => {
    const { data: res } = await api.get(`/orders/${o.id}`);
    generateProformaInvoicePDF(res.data);
  };

  const openAssignModal = async (o) => {
    setAssignModal({ open: true, orderId: o.id, orderNum: o.order_number, ownerId: o.user_id });
    setAssignTab('assignments');
    setAssignForm({ user_id: '', access_level: 'read_only', notes: '' });
    setAssignError(''); setWlForm({ log_type: 'note', log_text: '' }); setWlError('');
    const [aRes, wlRes, usersRes] = await Promise.all([
      api.get(`/orders/${o.id}/assignments`),
      api.get(`/orders/${o.id}/work-logs`),
      api.get('/users', { params: { limit: 200 } }).catch(() => ({ data: { data: [] } })),
    ]);
    setAssignments(aRes.data.data || []);
    setWorkLogs(wlRes.data.data || []);
    setAssignUsers(usersRes.data.data || []);
  };

  const handleAddAssignment = async () => {
    if (!assignForm.user_id) { setAssignError('Please select a user.'); return; }
    setAssignSaving(true); setAssignError('');
    try {
      await api.post(`/orders/${assignModal.orderId}/assignments`, assignForm);
      const { data: r } = await api.get(`/orders/${assignModal.orderId}/assignments`);
      setAssignments(r.data || []);
      setAssignForm({ user_id: '', access_level: 'read_only', notes: '' });
    } catch (err) { setAssignError(err.response?.data?.message || 'Failed to add assignment.'); }
    finally { setAssignSaving(false); }
  };

  const handleUpdateAssignment = async (assignId, access_level) => {
    try {
      await api.put(`/orders/${assignModal.orderId}/assignments/${assignId}`, { access_level });
      setAssignments(prev => prev.map(a => a.id === assignId ? { ...a, access_level } : a));
    } catch (err) { setAssignError(err.response?.data?.message || 'Failed to update assignment.'); }
  };

  const handleRemoveAssignment = async (assignId, userName) => {
    if (!window.confirm(`Remove ${userName} from this order?`)) return;
    try {
      await api.delete(`/orders/${assignModal.orderId}/assignments/${assignId}`);
      setAssignments(prev => prev.filter(a => a.id !== assignId));
    } catch (err) { setAssignError(err.response?.data?.message || 'Failed to remove assignment.'); }
  };

  const handleAddWorkLog = async () => {
    if (!wlForm.log_text.trim()) { setWlError('Please enter a log entry.'); return; }
    setWlSaving(true); setWlError('');
    try {
      await api.post(`/orders/${assignModal.orderId}/work-logs`, wlForm);
      const { data: r } = await api.get(`/orders/${assignModal.orderId}/work-logs`);
      setWorkLogs(r.data || []);
      setWlForm({ log_type: 'note', log_text: '' });
    } catch (err) { setWlError(err.response?.data?.message || 'Failed to add work log.'); }
    finally { setWlSaving(false); }
  };

  const handleDeleteWorkLog = async (logId) => {
    if (!window.confirm('Delete this work log?')) return;
    try {
      await api.delete(`/orders/${assignModal.orderId}/work-logs/${logId}`);
      setWorkLogs(prev => prev.filter(l => l.id !== logId));
    } catch (err) { setWlError(err.response?.data?.message || 'Failed to delete work log.'); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Proforma Invoice Orders</h1>
          <p className="page-subtitle">View and download proforma invoice documents</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ padding: '5px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700, color: '#c2410c' }}>
            PROFORMA
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="search-input-wrap" style={{ flex: '1 1 220px' }}>
              <span className="search-icon">
                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </span>
              <input className="form-control" placeholder="Search order # or client…" value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="form-control form-select" style={{ width: 150 }} value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select>
            <input type="date" className="form-control" style={{ width: 150 }} value={fromDate}
              onChange={e => { setFromDate(e.target.value); setPage(1); }} title="From date" />
            <input type="date" className="form-control" style={{ width: 150 }} value={toDate}
              onChange={e => { setToDate(e.target.value); setPage(1); }} title="To date" />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Quote #</th>
                <th>Client</th>
                <th>Date</th>
                <th>Est. Delivery</th>
                <th>Est. Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8}><div className="loading-overlay"><div className="spinner" /></div></td></tr>
              )}
              {!loading && data.data.length === 0 && (
                <tr><td colSpan={8}><div className="empty-state"><p>No proforma invoice orders found.</p></div></td></tr>
              )}
              {!loading && data.data.map(o => (
                <tr key={o.id}>
                  <td style={{ fontWeight: 700, color: '#c2410c', fontSize: '0.82rem' }}>{o.order_number}</td>
                  <td style={{ fontSize: '0.83rem', color: 'var(--text-secondary)' }}>{o.quote_number || '—'}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{o.client_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{o.client_country || ''}</div>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    {new Date(o.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    {o.expected_delivery ? new Date(o.expected_delivery).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td style={{ fontWeight: 700 }}>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{o.currency} </span>
                    {Number(o.total || 0).toFixed(2)}
                    <div style={{ fontSize: '0.68rem', color: '#c2410c', fontWeight: 600 }}>estimate</div>
                  </td>
                  <td>
                    <span className={`badge badge-${statusColors[o.status] || 'neutral'}`} style={{ textTransform: 'capitalize' }}>
                      {o.status}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="btn btn-ghost btn-icon" data-tooltip="View Details" onClick={() => openView(o)}>
                        <EyeIcon />
                      </button>
                      <button className="btn btn-ghost btn-icon" data-tooltip="Download Proforma PDF" onClick={() => handleDownload(o)}>
                        <DownloadIcon />
                      </button>
                      <button className="btn btn-ghost btn-icon" data-tooltip="Assignments & Work Logs" style={{ color: 'var(--primary)' }} onClick={() => openAssignModal(o)}>
                        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={data.page} total={data.total} limit={data.limit} onPageChange={setPage} />
      </div>

      {viewOrder && <OrderDetailModal order={viewOrder} onClose={() => setViewOrder(null)} />}

      {/* ── Assignments & Work Logs Modal ─────────────────────── */}
      <Modal open={assignModal.open} onClose={() => setAssignModal({ open: false, orderId: null, orderNum: '', ownerId: null })}
        title={`Assignments — ${assignModal.orderNum || ''}`} size="md"
        footer={<button className="btn btn-secondary" onClick={() => setAssignModal({ open: false, orderId: null, orderNum: '', ownerId: null })}>Close</button>}
      >
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
          {['assignments', 'worklogs'].map(tab => (
            <button key={tab} onClick={() => setAssignTab(tab)}
              style={{ padding: '7px 18px', background: 'none', border: 'none', borderBottom: assignTab === tab ? '2px solid var(--primary)' : '2px solid transparent', fontWeight: assignTab === tab ? 700 : 400, color: assignTab === tab ? 'var(--primary)' : 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem' }}>
              {tab === 'assignments' ? 'Assignments' : 'Work Logs'}
            </button>
          ))}
        </div>

        {assignTab === 'assignments' && (
          <div>
            {assignments.length === 0
              ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px 0 16px' }}>No assignees yet.</div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                  {assignments.map(a => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{a.user_name}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{a.user_role} · Assigned by {a.assigned_by_name || '—'} · {new Date(a.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                        {a.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, fontStyle: 'italic' }}>{a.notes}</div>}
                      </div>
                      {(isPrivileged || user?.id === assignModal.ownerId)
                        ? <select className="form-control form-select" style={{ fontSize: '0.78rem', width: 120, padding: '3px 6px' }} value={a.access_level} onChange={e => handleUpdateAssignment(a.id, e.target.value)}>
                            <option value="read_only">Read Only</option>
                            <option value="read_write">Read + Write</option>
                          </select>
                        : <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{a.access_level === 'read_write' ? 'Read + Write' : 'Read Only'}</span>
                      }
                      {(isPrivileged || user?.id === assignModal.ownerId) && (
                        <button className="btn btn-ghost btn-icon btn-sm" data-tooltip="Remove" style={{ color: 'var(--error)' }} onClick={() => handleRemoveAssignment(a.id, a.user_name)}>
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
            }
            {(isPrivileged || user?.id === assignModal.ownerId || assignments.some(a => a.user_id === user?.id && a.access_level === 'read_write')) && (
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: '0.83rem', marginBottom: 10 }}>Add Assignee</div>
                {assignError && <div className="alert alert-error" style={{ marginBottom: 10, fontSize: '0.8rem', padding: '6px 10px' }}>{assignError}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 150 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>User</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={assignForm.user_id} onChange={e => setAssignForm(p => ({ ...p, user_id: e.target.value }))}>
                      <option value="">— Select user —</option>
                      {assignUsers.filter(u => u.id !== assignModal.ownerId && !assignments.some(a => a.user_id === u.id)).map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 130 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Access</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={assignForm.access_level} onChange={e => setAssignForm(p => ({ ...p, access_level: e.target.value }))}>
                      <option value="read_only">Read Only</option>
                      <option value="read_write">Read + Write</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 2, minWidth: 150 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Note (optional)</label>
                    <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="e.g. Handling dispatch…" value={assignForm.notes} onChange={e => setAssignForm(p => ({ ...p, notes: e.target.value }))} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={handleAddAssignment} disabled={assignSaving} style={{ whiteSpace: 'nowrap', marginBottom: 2 }}>{assignSaving ? 'Assigning…' : 'Assign'}</button>
                </div>
              </div>
            )}
          </div>
        )}

        {assignTab === 'worklogs' && (
          <div>
            {(isPrivileged || user?.id === assignModal.ownerId || assignments.some(a => a.user_id === user?.id && a.access_level === 'read_write')) && (
              <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: 14, border: '1px solid var(--border)', marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '0.83rem', marginBottom: 10 }}>Add Work Log</div>
                {wlError && <div className="alert alert-error" style={{ marginBottom: 10, fontSize: '0.8rem', padding: '6px 10px' }}>{wlError}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ marginBottom: 0, minWidth: 130 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Type</label>
                    <select className="form-control form-select" style={{ fontSize: '0.83rem' }} value={wlForm.log_type} onChange={e => setWlForm(p => ({ ...p, log_type: e.target.value }))}>
                      {['note','call','meeting','email','visit','follow_up','other'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Entry *</label>
                    <input className="form-control" style={{ fontSize: '0.83rem' }} placeholder="Describe what was done…" value={wlForm.log_text} onChange={e => setWlForm(p => ({ ...p, log_text: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddWorkLog(); } }} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={handleAddWorkLog} disabled={wlSaving} style={{ whiteSpace: 'nowrap', marginBottom: 2 }}>{wlSaving ? 'Saving…' : 'Log'}</button>
                </div>
              </div>
            )}
            {workLogs.length === 0
              ? <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px 0' }}>No work logs yet.</div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {workLogs.map(log => (
                    <div key={log.id} style={{ display: 'flex', gap: 10, padding: '9px 12px', background: 'var(--surface-2)', borderRadius: 8, border: '1px solid var(--border)', alignItems: 'flex-start' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary-lighter)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase' }}>{(log.user_name || '?')[0]}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{log.user_name}</span>
                          <span style={{ fontSize: '0.72rem', padding: '1px 7px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{log.log_type.replace('_', ' ')}</span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{new Date(log.logged_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div style={{ fontSize: '0.83rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{log.log_text}</div>
                      </div>
                      {(isPrivileged || log.user_id === user?.id || user?.id === assignModal.ownerId) && (
                        <button className="btn btn-ghost btn-icon btn-sm" data-tooltip="Delete" style={{ color: 'var(--error)', flexShrink: 0 }} onClick={() => handleDeleteWorkLog(log.id)}>
                          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
            }
          </div>
        )}
      </Modal>
    </div>
  );
}
